import type { DiscountValidationResult } from 'src/types/application/SalesOrderDraftDTO';

/**
 * Get the validated/ERP unit price from discount validation results.
 * Returns discount price if available, otherwise list price.
 */
export function getErpUnitPrice(
  discountResult?: DiscountValidationResult,
): number | null {
  // Use nullish check (not truthy) so that discountPrice of 0 is accepted
  if (discountResult?.discount?.discountPrice != null) {
    return discountResult.discount.discountPrice;
  }
  return discountResult?.listPrice?.price ?? null;
}

/**
 * Get the Oracle list price from discount validation results.
 */
export function getListPrice(
  discountResult?: DiscountValidationResult,
): number | null {
  return discountResult?.listPrice?.price ?? null;
}

/**
 * Calculate discount percentage from list price.
 * Returns the percentage discount (0-100 scale).
 * A positive value means the unit price is below list price.
 */
export function calculateDiscountPercent(
  unitPrice: number | null | undefined,
  listPrice: number | null | undefined,
): number | null {
  if (
    unitPrice == null ||
    listPrice == null ||
    listPrice === 0 ||
    isNaN(unitPrice) ||
    isNaN(listPrice)
  ) {
    return null;
  }
  return ((listPrice - unitPrice) / listPrice) * 100;
}

/**
 * Format a number as currency (USD).
 */
export function formatCurrency(
  value: number | string | null | undefined,
): string {
  if (value == null) {
    return '-';
  }
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) {
    return '-';
  }
  return `$${numValue.toFixed(2)}`;
}

/**
 * Format a number as a percentage with one decimal place.
 */
export function formatPercent(value: number | null | undefined): string {
  if (value == null || isNaN(value)) {
    return '-';
  }
  return `${value.toFixed(1)}%`;
}

/**
 * Calculate total price (quantity x unit price).
 */
export function calculateTotal(
  quantity: number | null | undefined,
  unitPrice: number | null | undefined,
): number | null {
  if (
    quantity == null ||
    unitPrice == null ||
    isNaN(quantity) ||
    isNaN(unitPrice)
  ) {
    return null;
  }
  return quantity * unitPrice;
}
